import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ClockIcon, EnvelopeIcon, DocumentTextIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface RegistrationHistory {
  registration_id: number;
  attendee_name: string;
  attendee_email: string;
  event_title: string;
  registration_logs: RegistrationLog[];
  email_logs: EmailLog[];
}

interface RegistrationLog {
  id: number;
  registration_id: number;
  action: string;
  old_status?: string;
  new_status?: string;
  details?: string;
  performed_by?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  email_log?: EmailLog;
}

interface EmailLog {
  id: number;
  registration_id: number;
  registration_log_id?: number;
  email_type: string;
  recipient_email: string;
  subject: string;
  postfix_queue_id?: string;
  smtp_response?: string;
  status: string;
  template_used?: string;
  custom_message: boolean;
  sent_at: string;
  delivered_at?: string;
  bounced_at?: string;
}

interface RegistrationHistoryProps {
  registrationId: number;
  onClose?: () => void;
}

const RegistrationHistoryComponent: React.FC<RegistrationHistoryProps> = ({ registrationId, onClose }) => {
  const [history, setHistory] = useState<RegistrationHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory();
  }, [registrationId]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/logs/registration/${registrationId}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }

      const data = await response.json();
      setHistory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string, log?: RegistrationLog) => {
    // For email actions, check if it failed
    const isEmailAction = ['email_sent', 'invitation_sent', 'verification_sent', 'approval_sent'].includes(action);
    const emailFailed = isEmailAction && log?.email_log?.status === 'failed';
    
    switch (action) {
      case 'created':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'status_changed':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'email_sent':
      case 'invitation_sent':
      case 'verification_sent':
      case 'approval_sent':
        return <EnvelopeIcon className={`h-5 w-5 ${emailFailed ? 'text-red-500' : 'text-purple-500'}`} />;
      case 'deleted':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getActionDescription = (log: RegistrationLog) => {
    let description = '';
    
    switch (log.action) {
      case 'created':
        description = 'Registration created';
        break;
      case 'status_changed':
        description = `Status changed from ${log.old_status} to ${log.new_status}`;
        break;
      case 'email_verified':
        description = 'Email address verified';
        break;
      case 'invitation_sent':
        description = 'Invitation email sent';
        break;
      case 'invitation_accepted':
        description = 'Invitation accepted';
        break;
      case 'invitation_declined':
        description = 'Invitation declined';
        break;
      case 'invitation_opened':
        description = 'Invitation opened';
        break;
      case 'checked_in':
        description = 'Checked in at event';
        break;
      case 'checked_out':
        description = 'Checked out from event';
        break;
      case 'verification_sent':
        description = 'Verification email sent';
        break;
      case 'approval_sent':
        description = 'Approval email sent';
        break;
      default:
        description = log.action.replace(/_/g, ' ');
    }
    
    // Add email-specific information
    if (log.email_log && ['invitation_sent', 'verification_sent', 'approval_sent', 'email_sent'].includes(log.action)) {
      if (log.email_log.status === 'failed') {
        // For failed emails, show the error
        const errorMsg = log.email_log.smtp_response?.replace('ERROR: ', '') || 'Unknown error';
        description += ` - Failed: ${errorMsg}`;
      } else if (log.email_log.postfix_queue_id) {
        // For successful emails, show the queue ID
        description += ` (${log.email_log.postfix_queue_id})`;
      }
    }
    
    return description;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !history) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <p className="text-red-600">{error || 'Failed to load history'}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Registration History</h2>
              <p className="text-sm text-gray-600 mt-1">{history.attendee_name} - {history.attendee_email}</p>
              <p className="text-sm text-gray-500">{history.event_title}</p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-200px)]">
        <div className="flow-root">
          <ul className="-mb-8">
            {history.registration_logs.map((log, idx) => (
              <li key={log.id}>
                <div className="relative pb-8">
                  {idx !== history.registration_logs.length - 1 ? (
                    <span
                      className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white bg-gray-100">
                        {getActionIcon(log.action, log)}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-900">
                            {getActionDescription(log)}
                          </p>
                          {/* Email status badge */}
                          {log.email_log && ['invitation_sent', 'verification_sent', 'approval_sent', 'email_sent'].includes(log.action) && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              log.email_log.status === 'failed' 
                                ? 'bg-red-100 text-red-800' 
                                : log.email_log.status === 'delivered'
                                ? 'bg-green-100 text-green-800'
                                : log.email_log.status === 'bounced'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {log.email_log.status}
                            </span>
                          )}
                        </div>
                        {/* Email template info */}
                        {log.email_log?.template_used && (
                          <p className="mt-1 text-xs text-gray-500">
                            Template: {log.email_log.template_used}
                            {log.email_log.custom_message && ' (customized)'}
                          </p>
                        )}
                        {log.details && (
                          <p className="mt-1 text-xs text-gray-500">
                            {(() => {
                              try {
                                const details = JSON.parse(log.details);
                                return Object.entries(details)
                                  .filter(([key]) => key !== 'migration')
                                  .map(([key, value]) => `${key}: ${value}`)
                                  .join(', ');
                              } catch {
                                return log.details;
                              }
                            })()}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          by {log.performed_by || 'Unknown'}
                          {log.ip_address && ` from ${log.ip_address}`}
                        </p>
                      </div>
                      <div className="whitespace-nowrap text-right text-xs text-gray-500">
                        <time dateTime={log.created_at}>
                          {format(new Date(log.created_at), 'MMM d, yyyy HH:mm')}
                        </time>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
    </div>
  );
};

export default RegistrationHistoryComponent;